/**
 * The account, as the server sees it.
 *
 * Read-only: names and passwords are set at registration, and there is no
 * endpoint to change them later. What this screen answers is "which account am
 * I signed in as, and what does the server believe about it" - the address,
 * whether it is verified, and the recognition default the server ships.
 */

import type { Api } from '../api/http.js';
import type { User } from '../api/types.js';
import { Banner } from '../components/Banner.js';
import { useAsync } from '../state/useAsync.js';

export interface ProfileScreenProps {
  api: Api;
  user: User;
  onSignOut: () => void;
}

export function ProfileScreen({ api, user, onSignOut }: ProfileScreenProps) {
  const [fresh, reload] = useAsync(() => api.me(), [api]);
  const shown = fresh.status === 'ready' ? fresh.value : user;

  return (
    <div className="stack">
      <header>
        <h2>Profile</h2>
        <p className="hint">The account on this device, as the server sees it.</p>
      </header>

      {fresh.status === 'error' && <Banner kind="error" onRetry={reload}>{fresh.message}</Banner>}

      <div className="card">
        <div className="container-title">
          <h3>
            <span className="bin-name">{shown.displayName}</span>{' '}
            <span className="chip">{shown.emailVerified ? 'verified' : 'unverified'}</span>
          </h3>
          <span className="hint tnum">{shown.email}</span>
        </div>
        <p className="hint">
          {shown.emailVerified
            ? 'The address is verified.'
            : 'The address is not verified yet: proving ownership is a verification email, which this server does not send.'}
        </p>
        <p className="hint">
          The server files scans with <strong>{shown.defaultRecognitionMode}</strong> recognition unless a batch says
          otherwise.
        </p>
        <div className="form-foot">
          <button type="button" className="ghost danger-text" onClick={onSignOut}>
            Log out
          </button>
        </div>
      </div>
    </div>
  );
}
